import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Optional,
  Output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialogRef } from "@angular/material/dialog";
import { defer, finalize, first, Observable } from "rxjs";

@Component({
  selector: "app-shared-dialog",
  templateUrl: "./shared-dialog.component.html",
  styleUrls: ["./shared-dialog.component.scss"],
  standalone: false,
})
export class SharedDialogComponent {
  @Input() title = "";
  @Input() confirmLabel = "Confirm";
  @Input() cancelLabel = "Cancel";
  @Input() destructive = false;
  @Input() confirmable = true;
  @Input() confirmAction?: () => Observable<unknown>;
  @Input() pendingLabel = "Working...";
  @Input() failureMessage = "Unable to complete this action. Please try again.";
  @Output() confirmError = new EventEmitter<unknown>();

  readonly pending = signal(false);
  readonly failed = signal(false);
  private readonly destroyRef = inject(DestroyRef);

  constructor(@Optional() private dialogRef: MatDialogRef<unknown, boolean>) {}

  confirm(): void {
    if (this.pending() || !this.confirmable) return;
    if (!this.confirmAction) {
      this.close(true);
      return;
    }

    const previousDisableClose = this.dialogRef?.disableClose ?? false;
    this.pending.set(true);
    this.failed.set(false);
    if (this.dialogRef) this.dialogRef.disableClose = true;

    defer(this.confirmAction)
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.pending.set(false);
          if (this.dialogRef) {
            this.dialogRef.disableClose = previousDisableClose;
          }
        }),
      )
      .subscribe({
        next: () => this.dialogRef?.close(true),
        error: (error: unknown) => {
          this.failed.set(true);
          this.confirmError.emit(error);
        },
      });
  }

  close(result: boolean): void {
    if (this.pending()) return;
    this.dialogRef?.close(result);
  }
}
